import React, { useMemo } from "react";
import { Button, Card, Col, Form, OverlayTrigger, Popover, Row } from "react-bootstrap";
import Select from "react-select";
import { ReactComponent as Info } from "../../../../images/Info.svg";
import "./SLMSpaceInformationPanel.css";

const getLookupList = (slmLookups, lookupKey) => {
  if (!Array.isArray(slmLookups)) {
    return [];
  }

  const lookupGroup = slmLookups.find((item) => item?.look_up === lookupKey);
  return Array.isArray(lookupGroup?.list) ? lookupGroup.list : [];
};

const getLookupListByCandidates = (slmLookups, lookupKeys) => {
  for (const lookupKey of lookupKeys) {
    const list = getLookupList(slmLookups, lookupKey);
    if (list.length > 0) {
      return list;
    }
  }

  return [];
};

const mapLookupList = (list, candidateValueKeys, candidateLabelKeys) => {
  return list
    .map((option) => {
      if (typeof option === "string") {
        return { value: option, label: option };
      }

      const value =
        candidateValueKeys
          .map((key) => option?.[key])
          .find((v) => v !== undefined && v !== null && String(v) !== "") ?? "";

      const label =
        candidateLabelKeys
          .map((key) => option?.[key])
          .find((v) => v !== undefined && v !== null && String(v) !== "") ?? String(value);

      return {
        ...option,
        value,
        label,
      };
    })
    .filter((option) => option.value !== undefined && option.value !== null && String(option.value) !== "");
};

export default function SLMSpaceInformationPanel({
  slmLookups,
  selectedLeaseStatusCategories,
  selectedBroadSpaceUseCategories,
  selectedSpaceUseCategories,
  selectedLeaseMethodCategories,
  selectedLevelCategories,
  selectedCarSpaceCategories,
  selectedQualityTypeCategories,
  leaseStatusOptions,
  broadSpaceUseOptions,
  detailedSpaceUseOptions,
  leaseMethodOptions,
  levelOptions,
  carSpaceOptions,
  qualityTypeOptions,
  broadSpaceUseId,
  tenantArea,
  leaseLevel,
  cars,
  leaseNotes,
  leaseNotesComputed,
  noteData,
  printMonth,
  onStatusChange,
  onBroadSpaceUseChange,
  onSpaceUseChange,
  onLeaseMethodChange,
  onLevelCategoryChange,
  onCarSpaceChange,
  onQualityTypeChange,
  onNumberChange,
  onChange,
  onBlur,
  createId,
  goLiveDate,
}) {
  const derivedLeaseStatusOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "lease_status_category"),
      ["value", "lease_status_cat_id", "lease_status_category_id", "id", "code", "name", "label"],
      ["label", "lease_status_cat", "lease_status_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : leaseStatusOptions;
  }, [slmLookups, leaseStatusOptions]);

  const derivedBroadSpaceUseOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "broad_space_use"),
      ["value", "broad_space_use_id", "id", "code", "name", "label"],
      ["label", "broad_space_use_code", "broad_space_use", "name", "description", "code", "value"]
    );

    const baseOptions = lookupValues.length > 0 ? lookupValues : broadSpaceUseOptions;
    return baseOptions.filter((option) => option.value === broadSpaceUseId || broadSpaceUseId === null || broadSpaceUseId === 1);
  }, [slmLookups, broadSpaceUseOptions, broadSpaceUseId]);

  const derivedLeaseMethodOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "lease_type_category"),
      ["value", "lease_type_cat_id", "lease_type_category_id", "id", "code", "name", "label"],
      ["label", "lease_type_cat", "lease_type_category", "lease_type", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : leaseMethodOptions;
  }, [slmLookups, leaseMethodOptions]);

  const derivedDetailedSpaceUseOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupListByCandidates(slmLookups, ["space_use", "space_use_category", "spaceusecategory"]),
      ["value", "space_use_id", "space_use_category_id", "id", "code", "name", "label"],
      ["label", "space_use_name", "space_use_code", "space_use", "name", "description", "code", "value"]
    );

    const baseOptions = lookupValues.length > 0 ? lookupValues : detailedSpaceUseOptions || [];
    return baseOptions.filter((option) => {
      if (broadSpaceUseId === null || broadSpaceUseId === undefined) {
        return true;
      }

      const optionBroadSpaceUseId = option?.supplementary_details?.broad_space_use_id;
      return String(optionBroadSpaceUseId ?? "") === String(broadSpaceUseId);
    });
  }, [slmLookups, detailedSpaceUseOptions, broadSpaceUseId]);

  const derivedLevelOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupListByCandidates(slmLookups, ["level_description_category", "lease_level_category", "leaselevelcategory", "level_description"]),
      ["value", "level_description_cat_id", "lease_level_cat_id", "id", "code", "name", "label"],
      ["label", "level_description_cat", "level_description", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : levelOptions || [];
  }, [slmLookups, levelOptions]);

  const derivedCarSpaceOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupListByCandidates(slmLookups, ["car_space_describer", "car_space_descr", "car_space_category", "carspacecategory"]),
      ["value", "car_space_descr_cat_id", "car_space_category_id", "id", "code", "name", "label"],
      ["label", "car_space_descr_cat", "car_space_describer", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : carSpaceOptions || [];
  }, [slmLookups, carSpaceOptions]);

  const derivedQualityTypeOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupListByCandidates(slmLookups, ["quality_type", "quality_type_category", "qualitytypecategory"]),
      ["value", "quality_type_id", "quality_type_category_id", "id", "code", "name", "label"],
      ["label", "quality_type", "quality_type_name", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : qualityTypeOptions || [];
  }, [slmLookups, qualityTypeOptions]);

  return (
    <Card className="mb-2">
      <Card.Header>Space Information</Card.Header>
      <Card.Body>
        <Form.Group as={Row} controlId="lease_status_cat">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Status (existing/precom)
          </Form.Label>
          <Col>
            <Select value={selectedLeaseStatusCategories} onChange={onStatusChange} options={derivedLeaseStatusOptions} inputId={createId("field", "lease-status", "input")} instanceId={createId("field", "lease-status", "instance")} isClearable />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="broad_space_use_name">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Broad Space Use
          </Form.Label>
          <Col>
            <Select value={selectedBroadSpaceUseCategories} onChange={onBroadSpaceUseChange} options={derivedBroadSpaceUseOptions} inputId={createId("field", "broad-space-use", "input")} instanceId={createId("field", "broad-space-use", "instance")} isClearable />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="space_use_name">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Detailed Space Use
          </Form.Label>
          <Col>
            <Select
              value={selectedSpaceUseCategories}
              onChange={onSpaceUseChange}
              options={derivedDetailedSpaceUseOptions}
              inputId={createId("field", "detailed-space-use", "input")}
              instanceId={createId("field", "detailed-space-use", "instance")}
              isClearable
            />
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="lease_method">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Lease Method/Type
          </Form.Label>
          <Col>
            <Select value={selectedLeaseMethodCategories} onChange={onLeaseMethodChange} options={derivedLeaseMethodOptions} inputId={createId("field", "lease-method", "input")} instanceId={createId("field", "lease-method", "instance")} isClearable />
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="tenant_area">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Area SQM
          </Form.Label>
          <Col>
            <Form.Control id={createId("field", "tenant-area", "input")} name="tenant_area" onChange={onNumberChange} value={tenantArea} as="input" type="text" onBlur={onBlur} />
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="level_description">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Level Description
          </Form.Label>
          <Col>
            <Select value={selectedLevelCategories} onChange={onLevelCategoryChange} options={derivedLevelOptions} inputId={createId("field", "level-description", "input")} instanceId={createId("field", "level-description", "instance")} isClearable />
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="lease_level">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Suite/Level
          </Form.Label>
          <Col>
            <Form.Control id={createId("field", "suite-level", "input")} name="lease_level" onChange={onChange} value={leaseLevel} as="input" type="text" onBlur={onBlur} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="cars">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Car Bays
          </Form.Label>
          <Col>
            <Form.Control id={createId("field", "car-bays", "input")} name="cars" onChange={onNumberChange} value={cars} as="input" type="text" onBlur={onBlur} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="car_space_describer">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Parking Describer
          </Form.Label>
          <Col>
            <Select value={selectedCarSpaceCategories} onChange={onCarSpaceChange} options={derivedCarSpaceOptions} inputId={createId("field", "parking-describer", "input")} instanceId={createId("field", "parking-describer", "instance")} isClearable />
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="quality_type">
          <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
            Quality
          </Form.Label>
          <Col>
            <Select value={selectedQualityTypeCategories} onChange={onQualityTypeChange} options={derivedQualityTypeOptions} inputId={createId("field", "quality", "input")} instanceId={createId("field", "quality", "instance")} isClearable />
          </Col>
        </Form.Group>

        <Form.Group as={Row} controlId="lease_notes">
          <Form.Label>
            <b>Extra lease notes (appear to customers)</b>
          </Form.Label>
          <Form.Control id={createId("field", "lease-notes", "input")} name="lease_notes" onChange={onChange} onBlur={onBlur} value={leaseNotes} as="textarea" type="text" rows="2" />
        </Form.Group>

        <Form.Group as={Row} controlId="lease_notes_computed">
          <Form.Label
            style={{
              width: "100%",
              marginTop: "5px",
            }}
          >
            <div className="slm-new-lease-notes-header-row">
              <div className="slm-new-lease-notes-title-wrap">
                <b>New auto-generated Lease Notes</b>
              </div>
              <div className="slm-new-lease-notes-right-wrap">
                {goLiveDate <= new Date(printMonth) && (
                  <b className="slm-new-lease-notes-status-text">
                    **Currently visible to customers**
                  </b>
                )}
                <OverlayTrigger
                  trigger="click"
                  key="info"
                  placement="top"
                  overlay={
                    <Popover
                      id={createId("popover", "lease-notes-info")}
                      style={{
                        maxWidth: "800px",
                      }}
                    >
                      <Popover.Header as="h3">Info</Popover.Header>
                      <Popover.Body>
                        {`In April 2023 - as part of the changes required for Commercial APIs - we moved the entry of lots/plans, volumes/folios and Transfer Number from the free-text Lease Notes, into separate Parcels and Titles References tables. 
We also introduced a "New auto-generated Lease Notes" field - which automatically creates Lease Notes text for PIM customers. 
We also retained the "Old Lease Notes - pre API 2023 changes".
Which does the customer see?
For older records with a Delivery Date earlier than the changes introduced April 2023, by default the customer sees the "Old Lease Notes - pre API 2023 changes". 
For new records since April 2023, the customer will see the "New auto-generated Lease Notes".
This also applies for older records redelivered since April 2023.
To make it clear which the customer is seeing, the text "**Currently visible to customers**" appears.
Note: When a new record is entered or an older record is redelivered, the onus is on the user to check the "New auto-generated Lease Notes" field appears correct.
The "New auto-generated Lease Notes" field cannot be edited directly and is built from Parcels, Volume/Folios, Transfer Number (where SOLD) and any entered "Extra lease notes".`}
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button
                    id={createId("action", "lease-notes-info", "button")}
                    className="d-inline-flex align-items-center justify-content-center slm-new-lease-notes-info-btn"
                    variant="link"
                    size="sm"
                    aria-label="Lease notes info"
                  >
                    {<Info />}
                  </Button>
                </OverlayTrigger>
              </div>
            </div>
          </Form.Label>
          <Form.Control
            id={createId("field", "lease-notes-computed", "input")}
            name="lease_notes_computed"
            onChange={onChange}
            onBlur={onBlur}
            value={typeof leaseNotesComputed === "string" ? leaseNotesComputed.trim() : leaseNotesComputed}
            as="textarea"
            type="text"
            rows="5"
            disabled
          />
        </Form.Group>

        <Form.Group as={Row} controlId="note_data">
          <Form.Label
            style={{
              width: "100%",
              paddingLeft: 0,
              paddingRight: 0,
              marginBottom: "4px",
            }}
          >
            <div
              className="slm-old-lease-notes-header-row"
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                flexWrap: "wrap",
                columnGap: "4px",
                rowGap: "2px",
              }}
            >
              <div
                className="slm-old-lease-notes-title-wrap"
                style={{
                  flex: "1 1 0",
                  minWidth: 0,
                  textAlign: "left",
                }}
              >
                <b>Old Lease Notes - pre API 2023 changes</b>
              </div>
              {goLiveDate > new Date(printMonth) && (
                <div
                  className="slm-old-lease-notes-status-wrap"
                  style={{
                    flex: "0 1 auto",
                    minWidth: 0,
                    marginLeft: "auto",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <b
                    className="slm-old-lease-notes-status-text"
                    style={{
                      color: "red",
                      textAlign: "right",
                    }}
                  >
                    **Currently visible to customers**
                  </b>
                </div>
              )}
            </div>
          </Form.Label>
          <Form.Control id={createId("field", "old-lease-notes", "input")} name="note_data" onChange={onChange} onBlur={onBlur} value={noteData} as="textarea" type="text" rows="5" disabled={true} />
        </Form.Group>
      </Card.Body>
    </Card>
  );
}
